/**
 * Maps zone polygon/line points between:
 * - Canvas internal space (fixed bitmap used by ZoneDrawingCanvas), and
 * - Stream / API pixel space (native video or image resolution expected by POST /zones).
 *
 * Accounts for CSS `object-fit: contain` letterboxing: only the video/image content area maps
 * 1:1 to stream pixels, not the full overlay box.
 */

export const ZONE_MARK_CANVAS_WIDTH = 600;
export const ZONE_MARK_CANVAS_HEIGHT = 400;

/** Parsed from API `resolution` (e.g. "1920X1080", "600X400"). */
export function parseZoneResolutionString(
  raw: string | null | undefined,
): { width: number; height: number } | null {
  if (raw == null || typeof raw !== "string") return null;
  const m = raw.trim().match(/^(\d+)\s*[xX]\s*(\d+)$/);
  if (!m) return null;
  const width = Number(m[1]);
  const height = Number(m[2]);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }
  return { width, height };
}

/**
 * The inner rectangle where `object-fit: contain` actually draws the bitmap inside the element.
 */
export function getObjectFitContainContentRect(
  containerWidth: number,
  containerHeight: number,
  intrinsicWidth: number,
  intrinsicHeight: number,
): { offsetX: number; offsetY: number; contentW: number; contentH: number } {
  if (
    containerWidth <= 0 ||
    containerHeight <= 0 ||
    intrinsicWidth <= 0 ||
    intrinsicHeight <= 0
  ) {
    return {
      offsetX: 0,
      offsetY: 0,
      contentW: Math.max(containerWidth, 0),
      contentH: Math.max(containerHeight, 0),
    };
  }
  const ir = intrinsicWidth / intrinsicHeight;
  const cr = containerWidth / containerHeight;
  if (cr > ir) {
    const contentH = containerHeight;
    const contentW = containerHeight * ir;
    return {
      offsetX: (containerWidth - contentW) / 2,
      offsetY: 0,
      contentW,
      contentH,
    };
  }
  const contentW = containerWidth;
  const contentH = containerWidth / ir;
  return {
    offsetX: 0,
    offsetY: (containerHeight - contentH) / 2,
    contentW,
    contentH,
  };
}

export function getIntrinsicMediaSize(
  el: HTMLVideoElement | HTMLImageElement | null,
): { width: number; height: number } {
  if (!el) return { width: 0, height: 0 };
  if (el instanceof HTMLVideoElement) {
    return { width: el.videoWidth || 0, height: el.videoHeight || 0 };
  }
  return { width: el.naturalWidth || 0, height: el.naturalHeight || 0 };
}

/**
 * Canvas bitmap (600×400) → stream pixels for API.
 * The Mark Zones overlay is sized/positioned to match the `object-fit: contain` content rect only,
 * so normalized canvas (u,v) maps linearly to stream pixels.
 */
export function canvasPointToStreamPixel(
  canvasX: number,
  canvasY: number,
  _containerRect: DOMRect,
  intrinsicWidth: number,
  intrinsicHeight: number,
  canvasInternalW = ZONE_MARK_CANVAS_WIDTH,
  canvasInternalH = ZONE_MARK_CANVAS_HEIGHT,
): { x: number; y: number } {
  const iw = intrinsicWidth > 0 ? intrinsicWidth : 1920;
  const ih = intrinsicHeight > 0 ? intrinsicHeight : 1080;
  const u = canvasX / canvasInternalW;
  const v = canvasY / canvasInternalH;
  const uu = Math.max(0, Math.min(1, u));
  const vv = Math.max(0, Math.min(1, v));
  return {
    x: uu * iw,
    y: vv * ih,
  };
}

/**
 * API stream pixels → canvas bitmap for overlay drawing.
 * `streamRefWidth/Height` come from stored `resolution` (coordinate space of the saved points).
 * Overlay matches `object-fit: contain` content rect — inverse of `canvasPointToStreamPixel`.
 */
export function streamPixelToCanvasPoint(
  streamX: number,
  streamY: number,
  streamRefWidth: number,
  streamRefHeight: number,
  _containerRect: DOMRect,
  _intrinsicWidth: number,
  _intrinsicHeight: number,
  canvasInternalW = ZONE_MARK_CANVAS_WIDTH,
  canvasInternalH = ZONE_MARK_CANVAS_HEIGHT,
): { x: number; y: number } {
  const u = streamRefWidth > 0 ? streamX / streamRefWidth : 0;
  const v = streamRefHeight > 0 ? streamY / streamRefHeight : 0;
  const uu = Math.max(0, Math.min(1, u));
  const vv = Math.max(0, Math.min(1, v));
  return {
    x: uu * canvasInternalW,
    y: vv * canvasInternalH,
  };
}

export function mapCanvasPointsToStreamCoordinates(
  points: Array<{ x: number; y: number }>,
  containerRect: DOMRect | null,
  intrinsicWidth: number,
  intrinsicHeight: number,
): number[][] {
  const iw = intrinsicWidth > 0 ? intrinsicWidth : 1920;
  const ih = intrinsicHeight > 0 ? intrinsicHeight : 1080;
  if (!containerRect || intrinsicWidth <= 0 || intrinsicHeight <= 0) {
    return points.map((p) => [
      (p.x / ZONE_MARK_CANVAS_WIDTH) * iw,
      (p.y / ZONE_MARK_CANVAS_HEIGHT) * ih,
    ]);
  }
  return points.map((p) => {
    const s = canvasPointToStreamPixel(
      p.x,
      p.y,
      containerRect,
      intrinsicWidth,
      intrinsicHeight,
    );
    return [s.x, s.y];
  });
}

export function mapStreamCoordinatesToCanvasPoints(
  pairs: Array<{ x: number; y: number }>,
  storedResolution: { width: number; height: number } | null,
  containerRect: DOMRect | null,
  intrinsicWidth: number,
  intrinsicHeight: number,
): Array<{ x: number; y: number }> {
  const isLegacyCanvasResolution =
    storedResolution != null &&
    storedResolution.width === ZONE_MARK_CANVAS_WIDTH &&
    storedResolution.height === ZONE_MARK_CANVAS_HEIGHT;

  if (isLegacyCanvasResolution) {
    if (!containerRect || intrinsicWidth <= 0 || intrinsicHeight <= 0) {
      return pairs.map((p) => ({ x: p.x, y: p.y }));
    }
    const W = containerRect.width;
    const H = containerRect.height;
    const content = getObjectFitContainContentRect(
      W,
      H,
      intrinsicWidth,
      intrinsicHeight,
    );
    return pairs.map((p) => {
      const dispX = (p.x / ZONE_MARK_CANVAS_WIDTH) * W;
      const dispY = (p.y / ZONE_MARK_CANVAS_HEIGHT) * H;
      let u = (dispX - content.offsetX) / content.contentW;
      let v = (dispY - content.offsetY) / content.contentH;
      u = Math.max(0, Math.min(1, u));
      v = Math.max(0, Math.min(1, v));
      return {
        x: u * ZONE_MARK_CANVAS_WIDTH,
        y: v * ZONE_MARK_CANVAS_HEIGHT,
      };
    });
  }

  const sw = storedResolution?.width ?? intrinsicWidth;
  const sh = storedResolution?.height ?? intrinsicHeight;
  if (!containerRect || intrinsicWidth <= 0 || intrinsicHeight <= 0) {
    const swF = sw > 0 ? sw : 1920;
    const shF = sh > 0 ? sh : 1080;
    return pairs.map((p) => ({
      x: (p.x / swF) * ZONE_MARK_CANVAS_WIDTH,
      y: (p.y / shF) * ZONE_MARK_CANVAS_HEIGHT,
    }));
  }

  return pairs.map((p) =>
    streamPixelToCanvasPoint(
      p.x,
      p.y,
      sw,
      sh,
      containerRect,
      intrinsicWidth,
      intrinsicHeight,
    ),
  );
}
